import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { IconButton } from '@components/icon-button';
import { IconDirectionLeft } from '@components/icons/direction-left';
import { IconDirectionRight } from '@components/icons/direction-right';
import { IconDownload } from '@components/icons/download';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { Modal, useModal } from '@components/modal';
import { Previewer } from '@components/previewer';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { fetchAndDownload } from '@utils/download';

export type PreviewFile = {
  name: string;
  src: string;
  srcConverted?: string;
  mimeType?: string;
  originalFileName?: string;
};

export const usePreviewFileModal = () =>
  useModal(PreviewFileModal, { variant: 'FULLSCREEN', backgroundColor: 'backgroundPrimary' });

type PreviewFileProps = {
  formFile: PreviewFile;
  filesList?: PreviewFile[];
};

const PREVIEW_CONTAINER_ID = 'preview-file-container';

const PreviewerContainer = styled(Stack).attrs({
  align: 'center',
  justify: 'center',
})({
  alignSelf: 'stretch',
  margin: pxToRem(24),
});

const PreviewFileModal = ({ formFile, filesList }: PreviewFileProps) => {
  const { t } = useScopedTranslate(locale);
  const isPreviewedFile = (file: PreviewFile) => file.src === formFile.src;
  // Traversal only makes sense within the list the file came from.
  const files = filesList?.some(isPreviewedFile) ? filesList : [formFile];
  const [currentIndex, setCurrentIndex] = useState(() => files.findIndex(isPreviewedFile));
  const currentFile = files[currentIndex];

  const traverseFiles = (direction: 'prev' | 'next') => {
    const index =
      direction === 'prev'
        ? currentIndex === 0
          ? files.length - 1
          : currentIndex - 1
        : (currentIndex + 1) % files.length;
    setCurrentIndex(index);
  };

  const headerActions = (
    <Stack orientation="horizontal" space={6}>
      <Button
        iconLeft={<IconDownload />}
        text={t('download')}
        variant="secondary"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          fetchAndDownload(currentFile.src, currentFile.name);
        }}
      />
      {files.length > 1 && (
        <Stack orientation="horizontal" space={1}>
          <IconButton
            aria-label={t('previousFile')}
            dataTestid="preview-prev"
            icon={<IconDirectionLeft />}
            onClick={() => traverseFiles('prev')}
          />
          <IconButton
            aria-label={t('nextFile')}
            dataTestid="preview-next"
            icon={<IconDirectionRight />}
            onClick={() => traverseFiles('next')}
          />
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      <Modal.FullscreenHeader title={currentFile.name} actions={headerActions} />
      <Modal.FullscreenContent>
        <PreviewerContainer id={PREVIEW_CONTAINER_ID}>
          <Previewer
            url={currentFile.srcConverted || currentFile.src}
            fileName={currentFile.originalFileName || currentFile.name}
            mimeType={currentFile.mimeType}
            maxWidth={1000}
            parentId={PREVIEW_CONTAINER_ID}
            fallback={
              <EmptyState
                contentColor="textSecondary"
                header={t('unableToPreview')}
                content={t('emptyDescription')}
                headerColor="textPrimary"
                icon={<IconVisibilityHidden size={80} />}
                orientation="vertical"
                transparent
              />
            }
          />
        </PreviewerContainer>
      </Modal.FullscreenContent>
    </>
  );
};

const locale = {
  en: {
    pdfRendered: 'PDF rendered preview',
    download: 'Download file',
    previousFile: 'Previous file',
    nextFile: 'Next file',
    unableToPreview: 'Unable to preview document',
    emptyDescription:
      'Supported formats: pdf, doc, docx, txt, rtf.\nPlease note: you can still try downloading the file and\n opening it on your computer. ',
  },
};
